import { useEffect, useState } from "react"
import {
  MODAL_FORM_CLOSE_SPRINT_INITIAL_STATE,
  MODAL_FORM_CLOSE_SPRINT_MODE,
  MODAL_FORM_CLOSE_SPRINT_VALIDATIONS,
  CLOSE_TYPES,
} from "../../../../../consts/modules/clientReport.consts"
import { getValidLuxonDate } from "../../../../../utils/date.utils"
import {
  isNotEmpty,
  isArrayNotEmpty,
  validateFieldValue,
} from "../../../../../utils/validations.utils"
import _ from "lodash"
import {
  approveClientSprint,
  finishClientSprint,
  updateClientSprintTeamMemberPoints,
  createClientSprintMilestone,
} from "../../../../../services/clientReport.service"
import useReportFiltersContext from "../../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import { DateTime } from "luxon"

const useFinishClientSprintForm = ({ initialSprintData, visible, mode }) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(
    _.cloneDeep(MODAL_FORM_CLOSE_SPRINT_INITIAL_STATE)
  )
  const [validations, setValidations] = useState(
    _.cloneDeep(MODAL_FORM_CLOSE_SPRINT_VALIDATIONS)
  )
  const { clientReportSelectedClient } = useReportFiltersContext()

  useEffect(() => {
    if (!initialSprintData || !visible) {
      return
    }
    setFormData((prev) => ({
      ...prev,
      startDate:
        initialSprintData?.startDate ?
          getValidLuxonDate(initialSprintData.startDate, {})
        : prev.startDate,
      endDate:
        initialSprintData?.endDate ?
          getValidLuxonDate(initialSprintData.endDate, {})
        : prev.endDate,
      teamMembers:
        (
          (initialSprintData?.teamMemberPoints?.length > 0 &&
            initialSprintData?.sprintNumberByClient > 1) ||
          mode !== MODAL_FORM_CLOSE_SPRINT_MODE.FINISH
        ) ?
          initialSprintData?.teamMemberPoints
        : [],
      milestone: {
        totalPoints: initialSprintData?.milestone?.totalPoints ?? null,
        milestoneName: initialSprintData?.milestone?.milestoneName ?? "",
        nextImportantDate:
          initialSprintData?.milestone?.nextImportantDate ?
            DateTime.fromISO(
              DateTime.fromISO(initialSprintData.milestone.nextImportantDate, {
                zone: "utc",
              }).toISODate()
            ).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
          : null,
        nextImportantDateName:
          initialSprintData?.milestone?.nextImportantDateName ?? "",
      },
    }))
    setValidations((prev) => {
      const isApproveMode = mode === MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE
      return {
        ...prev,
        startDate: {
          ...prev.startDate,
          value:
            initialSprintData?.startDate ?
              getValidLuxonDate(initialSprintData.startDate, {})
            : prev.startDate.value,
        },
        endDate: {
          ...prev.endDate,
          value:
            initialSprintData?.endDate ?
              getValidLuxonDate(initialSprintData.endDate, {})
            : prev.endDate.value,
        },
        teamMembers: {
          ...prev.teamMembers,
          value:
            (
              (initialSprintData?.teamMemberPoints?.length > 0 &&
                initialSprintData?.sprintNumberByClient > 1) ||
              mode !== MODAL_FORM_CLOSE_SPRINT_MODE.FINISH
            ) ?
              initialSprintData?.teamMemberPoints.map((member) => ({
                ...member,
                value: "",
                errors: [],
                typeValidations: [isNotEmpty],
                validationsOptions: {},
              }))
            : [],
        },
        approvalDate: {
          ...prev.approvalDate,
          typeValidations: isApproveMode ? [isNotEmpty] : [],
        },
      }
    })
  }, [visible, initialSprintData])

  useEffect(() => {
    const endDate = formData.endDate
    const nextImportantDate = formData.milestone?.nextImportantDate

    if (endDate && nextImportantDate && endDate >= nextImportantDate) {
      setFormData((prev) => ({
        ...prev,
        milestone: {
          ...prev.milestone,
          nextImportantDate: null,
        },
      }))
    }
  }, [formData.endDate])

  useEffect(() => {
    const isTimeout = formData.closeType === CLOSE_TYPES.TIMEOUT
    const isApproveMode = mode === MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE

    if (!isTimeout && (formData.motivo || formData.additionalText)) {
      setFormData((prev) => ({
        ...prev,
        motivo: "",
        additionalText: "",
        requiresAdditionalText: false,
      }))
    }

    setValidations((prev) => {
      const currentTeamMembers = formData.teamMembers
      const currentMotivo = isTimeout ? formData.motivo : ""
      const currentAdditionalText = isTimeout ? formData.additionalText : ""

      const newValidations = {
        ...prev,

        startDate: {
          ...prev.startDate,
          typeValidations: [isNotEmpty],
          value: formData.startDate || prev.startDate.value,
          errors: [],
        },
        endDate: {
          ...prev.endDate,
          typeValidations: [isNotEmpty],
          value: formData.endDate || prev.endDate.value,
          errors: [],
        },

        teamMembers: {
          ...prev.teamMembers,
          typeValidations: isTimeout ? [] : [isArrayNotEmpty],
          value: currentTeamMembers,
          errors: [],
        },
        motivo: {
          ...prev.motivo,
          typeValidations: isTimeout ? [isNotEmpty] : [],
          value: currentMotivo,
          errors: [],
        },
        additionalText: {
          ...prev.additionalText,
          typeValidations:
            isTimeout && formData.requiresAdditionalText ? [isNotEmpty] : [],
          value: currentAdditionalText,
          errors: [],
        },
        approvalDate: {
          ...prev.approvalDate,
          typeValidations: isApproveMode ? [isNotEmpty] : [],
          value: formData.approvalDate || prev.approvalDate.value,
          errors: [],
        },
      }

      return newValidations
    })
  }, [formData.closeType, mode])

  useEffect(() => {
    const isTimeout = formData.closeType === CLOSE_TYPES.TIMEOUT

    if (isTimeout) {
      setValidations((prev) => {
        const newValidations = {
          ...prev,
          additionalText: {
            ...prev.additionalText,
            typeValidations:
              isTimeout && formData.requiresAdditionalText ? [isNotEmpty] : [],
            value: formData.additionalText || prev.additionalText.value,
          },
        }

        return newValidations
      })
    }
  }, [formData.requiresAdditionalText])

  const submitService = async () => {
    if (!isFormValid()) return
    if (mode === MODAL_FORM_CLOSE_SPRINT_MODE.FINISH) {
      const response = await finishClientSprint(
        initialSprintData?.id,
        clientReportSelectedClient,
        formData.teamMembers,
        formData.startDate,
        formData.endDate,
        formData.closeType,
        formData.motivo,
        formData.additionalText
      )

      const milestone = formData.milestone
      const hasMilestoneData =
        milestone?.totalPoints ||
        milestone?.milestoneName ||
        milestone?.nextImportantDate ||
        milestone?.nextImportantDateName

      if (hasMilestoneData && response?.success) {
        await createClientSprintMilestone({
          clientSprintId: initialSprintData?.id,
          totalPoints: milestone.totalPoints,
          milestoneName: milestone.milestoneName,
          nextImportantDate: milestone.nextImportantDate,
          nextImportantDateName: milestone.nextImportantDateName,
        })
      }

      return response
    } else if (mode === MODAL_FORM_CLOSE_SPRINT_MODE.APPROVE) {
      return approveClientSprint(
        initialSprintData?.id,
        formData.teamMembers,
        formData.startDate,
        formData.endDate,
        formData.approvalDate
      )
    } else if (mode === MODAL_FORM_CLOSE_SPRINT_MODE.FINISHED_EDIT) {
      return updateClientSprintTeamMemberPoints(
        initialSprintData?.id,
        formData.teamMembers
      )
    }
  }

  const resetForm = () => {
    setFormData(_.cloneDeep(MODAL_FORM_CLOSE_SPRINT_INITIAL_STATE))
    setValidations(_.cloneDeep(MODAL_FORM_CLOSE_SPRINT_VALIDATIONS))
  }

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    setValidations((prev) => validateFieldValue(prev, field, value))
  }
  const isFormValid = () => {
    const isTimeout = formData.closeType === CLOSE_TYPES.TIMEOUT

    const hasStartDate = formData.startDate && formData.startDate !== ""
    const hasEndDate = formData.endDate && formData.endDate !== ""

    if (!hasStartDate || !hasEndDate) {
      return false
    }

    if (isTimeout) {
      const hasMotivo = formData.motivo && formData.motivo !== ""
      if (!hasMotivo) return false

      if (formData.requiresAdditionalText) {
        const hasAdditionalText =
          formData.additionalText && formData.additionalText !== ""
        if (!hasAdditionalText) return false
      }
    } else {
      const hasTeamMembers =
        formData.teamMembers && formData.teamMembers.length > 0
      if (!hasTeamMembers) return false
    }

    return true
  }

  return {
    loading,
    setLoading,
    formData,
    setFormData,
    validations,
    setValidations,
    resetForm,
    submitService,
    updateField,
    isFormValid,
  }
}

export default useFinishClientSprintForm
